/**
 * Трекинги сделки CRM: треки с товарами (`GET /api/deals/{id}/tracks`), поиск заведённого трека по
 * номеру (`GET /api/tracks/find`), привязка вещей договора (`POST …/tracks/{track}/bind` — пишет
 * в боевую CRM через legacy), номер и накладная для поля ответа (`GET /api/tracks/{id}/chat`),
 * треки серии (`GET /api/serials/{serial}/tracks`). Трек заводит только Резерв.
 * Спека `docs/specs/2026-10-23-deal-tracks-design.md`.
 */
import { apiFetch } from "@/lib/api";

export type TrackProduct = { serial: number; name: string; photo: string | null };

/** Конец маршрута: салон (`name` «ЕКБ», `title` «Екатеринбург»), клиент или просто адрес. */
export type TrackSide = {
  kind: "salon" | "client" | "address" | "unknown";
  name: string;
  title: string;
  address: string;
  /** Отправитель у начала, получатель у конца. */
  person: string;
};

export type TrackDirectionKind = "to_client" | "from_client" | "between" | "from_salon" | "to_salon" | "other";

export type TrackDirection = { kind: TrackDirectionKind; label: string; from: TrackSide; to: TrackSide };

export type Track = {
  id: number;
  /** Номер службы как в CRM (`trackNumber`; у Яндекса — сервисный `…-udp`). */
  number: string | null;
  waybill: string | null;
  /** Номер для людей: у Яндекса — клиентский `trackWaybill`, иначе `number`. */
  display: string | null;
  /** Сервисный номер, если на виду другой (Яндекс). */
  service: string | null;
  url: string | null;
  /** Трек в личном кабинете службы — сотруднику. */
  account_url: string | null;
  direction: TrackDirection;
  /** Группа трека CRM: «Прокат», «Повторка»… */
  group: string;
  /** Срок доставки «доставят до». */
  due: string | null;
  company_id: number;
  company: string;
  category_id: number;
  category: string;
  status_id: number;
  status: string;
  status_color: string | null;
  cancelled: boolean;
  active: boolean;
  cdek_status: string | null;
  cdek_status_at: string | null;
  from: string;
  to: string;
  comment: string;
  user: string;
  created: string | null;
  sent: string | null;
  delivered: string | null;
  waybill_pdf: boolean;
  waybill_name: string | null;
  products: TrackProduct[];
  deals: number[];
};

export type DealTracks = { deal_id: number; tracks: Track[]; products: TrackProduct[]; can_edit: boolean };

export const dealTracksKey = (deal: number) => ["deal-tracks", deal] as const;
export const loadDealTracks = (deal: number) => apiFetch<DealTracks>(`/api/deals/${deal}/tracks`);

export const findTracks = (number: string) =>
  apiFetch<{ tracks: Track[] }>(`/api/tracks/find?number=${encodeURIComponent(number)}`).then((r) => r.tracks);

export const bindTrack = (deal: number, track: number, serials: number[]) =>
  apiFetch<DealTracks>(`/api/deals/${deal}/tracks/${track}/bind`, { method: "POST", body: JSON.stringify({ serials }) });

export const serialTracksKey = (serial: number) => ["serial-tracks", serial] as const;
export const loadSerialTracks = (serial: number) =>
  apiFetch<{ tracks: Track[] }>(`/api/serials/${serial}/tracks`).then((r) => r.tracks);

/** PDF накладной у Резерва: `inline` — открыть во вкладке (печать), иначе скачать. */
export const waybillUrl = (track: number, inline = false) => `/api/tracks/${track}/waybill${inline ? "?inline=1" : ""}`;

/** Для поля ответа «Чатов»: текст с номером и PDF накладной, если она есть у Резерва. */
export async function trackForChat(track: number): Promise<{ message: string; files: File[] }> {
  const d = await apiFetch<{ message: string; file: { name: string; mime: string; file: string } | null }>(`/api/tracks/${track}/chat`);
  if (!d.file) return { message: d.message, files: [] };
  const bytes = Uint8Array.from(atob(d.file.file), (ch) => ch.charCodeAt(0));
  return { message: d.message, files: [new File([bytes], d.file.name, { type: d.file.mime })] };
}

/** «20.10 12:17» из «2026-10-20 12:17». */
export const dt = (s: string | null) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}` : "");
